import React, { useRef, useState, useEffect } from 'react';
import Map, { Source, Layer, Popup, NavigationControl } from 'react-map-gl';
import { useParcelContext } from '../context/ParcelContext';
import styled from 'styled-components';

import { api } from '../services/api';
import { 
  createTilesetSource,
  createTilesetLayer,
  getTilesetConfigurations,
  validateTilesetUrl,
  validateTilesetSource
} from '../utils/tilesetUtils';
import MapStyleSelector from './MapStyleSelector';
import { FaLayerGroup, FaCompass, FaMapMarkedAlt } from 'react-icons/fa';

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

const MapControls = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ControlButton = styled.button`
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 12px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  
  &:hover {
    background: #f5f5f5;
  }
`;

const TilesetToggleButton = styled.button`
  background: white;
  border: 1px solid #ccc;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  cursor: pointer;
  margin-top: 8px;
  font-size: 18px;
  transition: background 0.2s, border-color 0.2s;
  &:hover {
    background: #f5f5f5;
    border-color: #888;
  }
  outline: none;
`;

const MapControlGroup = styled.div`
  position: absolute;
  top: 80px;
  left: 10px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const IconControlButton = styled.button`
  background: white;
  border: 1px solid #ccc;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  cursor: pointer;
  font-size: 18px;
  transition: background 0.2s, border-color 0.2s;
  &:hover {
    background: #f5f5f5;
    border-color: #888;
  }
  outline: none;
`;

const CheckboxContainer = styled.div`
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 8px 12px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  margin-top: 8px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  margin: 0;
`;

// Add new styled components for the legend modal
const LegendModal = styled.div`
  position: absolute;
  top: 200px;
  left: 20px;
  width: 250px;
  max-height: 400px;
  background: white;
  border: 1px solid #ccc;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 1000;
  overflow: hidden;
  cursor: move;
`;

const LegendHeader = styled.div`
  background: #f8f9fa;
  padding: 12px 16px;
  border-bottom: 1px solid #dee2e6;
  font-weight: 600;
  font-size: 14px;
  color: #495057;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LegendContent = styled.div`
  max-height: 320px;
  overflow-y: auto;
  padding: 8px 0;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 16px;
  font-size: 12px;
  color: #495057;
  &:hover {
    background: #f8f9fa;
  }
`;

// Update the LegendColorBox to accept dynamic opacity
const LegendColorBox = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 3px;
  margin-right: 10px;
  border: 1px solid #dee2e6;
  opacity: ${props => props.opacity || 1};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #495057;
  }
`;


const ParcelMap = ({ onViewStateChange, onSetParcelBorder, onGetMapBounds }) => {
  const { 
    selectedParcel, 
    setSelectedParcel, 
    filters, 
    searchSummary,
    loadParcelDetails,
    filteredParcels,
    assemblages,
    tilesetUrl
  } = useParcelContext();

  const [viewState, setViewState] = useState({
    longitude: -98.5795,
    latitude: 39.8283,
    zoom: 4
  });

  const [selectedParcelId, setSelectedParcelId] = useState(null);
  const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/light-v11');
  const [tilesetConfig, setTilesetConfig] = useState(null);
  const [parcelBorder, setParcelBorder] = useState(null);
  const [borderLoading, setBorderLoading] = useState(false);
  const [coordinateParcel, setCoordinateParcel] = useState(null);
  const [coordinateParcelLoading, setCoordinateParcelLoading] = useState(false);
  const mapRef = useRef();
  const [tilesetVisible, setTilesetVisible] = useState(true);
  const [zoningLayerVisible, setZoningLayerVisible] = useState(false);
  const [zoningLayerType, setZoningLayerType] = useState('zoning'); // 'zoning' or 'land_use_category'
  const [tileCategories, setTileCategories] = useState(null);
  const [vectorTileUrl, setVectorTileUrl] = useState(null);

  // Function to get current map bounds
  const getMapBounds = () => {
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      const bounds = map.getBounds();
      return [
        bounds.getWest(),  // min longitude
        bounds.getSouth(), // min latitude
        bounds.getEast(),  // max longitude
        bounds.getNorth()  // max latitude
      ];
    }
    return null;
  };

  // Expose setViewState to parent component
  useEffect(() => {
    if (onViewStateChange) {
      onViewStateChange(setViewState);
    }
  }, [onViewStateChange]);

  // Expose setParcelBorder to parent component
  useEffect(() => {
    if (onSetParcelBorder) {
      onSetParcelBorder(setParcelBorder);
    }
  }, [onSetParcelBorder]);

  // Expose getMapBounds to parent component
  useEffect(() => {
    if (onGetMapBounds) {
      onGetMapBounds(getMapBounds);
    }
  }, [onGetMapBounds]);
  // Add custom diamond icon to the map
  useEffect(() => {
    const createDiamondIcon = (color, strokeColor = '#fff') => {
      const canvas = document.createElement('canvas');
      canvas.width = 24;
      canvas.height = 24;
      const ctx = canvas.getContext('2d');
      
      // Clear canvas with transparent background
      ctx.clearRect(0, 0, 24, 24);
      
      // Draw diamond shape with better proportions
      ctx.fillStyle = color;
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 2;
      
      // Diamond path - more diamond-like shape
      ctx.beginPath();
      ctx.moveTo(12, 3);   // Top point
      ctx.lineTo(20, 12);  // Right point
      ctx.lineTo(12, 21);  // Bottom point
      ctx.lineTo(4, 12);   // Left point
      ctx.closePath();
      
      // Add shadow effect
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 3;
      ctx.shadowOffsetX = 1;
      ctx.shadowOffsetY = 1;
      
      ctx.fill();
      
      // Reset shadow for stroke
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      
      ctx.stroke();
      
      // Add a small highlight for 3D effect
      ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.beginPath();
      ctx.moveTo(12, 5);
      ctx.lineTo(18, 12);
      ctx.lineTo(12, 19);
      ctx.lineTo(6, 12);
      ctx.closePath();
      ctx.fill();
      
      return canvas;
    };

    const addDiamondIcon = () => {
      if (mapRef.current) {
        const map = mapRef.current.getMap();
        
        try {
          // Create a single diamond icon with white fill (will be colored via paint)
          const diamondIcon = createDiamondIcon('#ffffff');
          
          // Remove existing image first
          if (map.hasImage('diamond')) {
            map.removeImage('diamond');
          }
          
          // Add the image to the map
          map.addImage('diamond', diamondIcon);
          
          setDiamondIconAvailable(true);
        } catch (error) {
          console.error('Error adding diamond icon:', error);
          setDiamondIconAvailable(false);
        }
      }
    };

    // Add icon when map loads
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      if (map.loaded()) {
        addDiamondIcon();
      } else {
        map.on('load', addDiamondIcon);
      }
    }
  }, []);

  // Update selected parcel feature state
  useEffect(() => {
    if (mapRef.current && selectedParcelId) {
      const map = mapRef.current.getMap();
      
      // Check if the source exists before trying to set feature states
      if (map.getSource('parcel-centers')) {
              // Clear previous selection
      filteredParcels.forEach(parcel => {
        try {
          map.setFeatureState(
            { source: 'parcel-centers-source', id: parcel.id },
            { selected: false }
          );
        } catch (error) {
          console.warn('Could not set feature state for parcel:', parcel.id, error);
        }
      });
      
      // Set new selection
      try {
        map.setFeatureState(
          { source: 'parcel-centers-source', id: selectedParcelId },
          { selected: true }
        );
      } catch (error) {
        console.warn('Could not set feature state for selected parcel:', selectedParcelId, error);
      }
      }
    }
  }, [selectedParcelId, filteredParcels]);

  // Mapbox access token - replace with your token
  const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

  // Initialize tileset configuration
  useEffect(() => {
    if (MAPBOX_ACCESS_TOKEN) {
      if (tilesetUrl) {
        setTilesetConfig({
          tilesetUrl,
          sourceId: 'parcel-tileset',
          accessToken: MAPBOX_ACCESS_TOKEN
        });
      } else {
        const configs = getTilesetConfigurations(MAPBOX_ACCESS_TOKEN);
        // Use the first valid configuration
        const validConfig = configs.find(config => 
          config && config.tilesetUrl && validateTilesetUrl(config.tilesetUrl)
        );
        if (validConfig) {
          setTilesetConfig({ ...validConfig, sourceId: 'parcel-tileset' });
        } else {
          setTilesetConfig({
            tilesetUrl: 'mapbox://mapbox.satellite',
            sourceId: 'parcel-tileset',
            accessToken: MAPBOX_ACCESS_TOKEN
          });
        }
      }
    }
  }, [MAPBOX_ACCESS_TOKEN, tilesetUrl]);

  // Create tileset source and layer configurations
  let tilesetSource = tilesetConfig ? createTilesetSource(
    tilesetConfig.tilesetUrl, 
    tilesetConfig.sourceId, 
    MAPBOX_ACCESS_TOKEN
  ) : null;

  // If the primary tileset source creation fails, try a fallback
  if (!tilesetSource && MAPBOX_ACCESS_TOKEN) {
    console.warn('Primary tileset source creation failed, trying fallback');
    tilesetSource = createTilesetSource(
      'mapbox://mapbox.satellite',
      'fallback-satellite',
      MAPBOX_ACCESS_TOKEN
    );
  }

  // Validate the tileset source
  const isValidTilesetSource = tilesetSource && validateTilesetSource(tilesetSource);

  const tilesetLayer = isValidTilesetSource ? createTilesetLayer(tilesetSource.id) : null;

  // Add minzoom property to tileset layer to make it invisible when zoom < 10
  if (tilesetLayer) {
    tilesetLayer.minzoom = 14;
  }

  // Check if diamond icon is available
  const [diamondIconAvailable, setDiamondIconAvailable] = useState(false);

  // Parcel center point layer style
  const parcelCenterLayer = {
    id: 'parcel-centers',
    type: 'circle',
    paint: {
      'circle-radius': 8,
      'circle-color': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        '#ff4757',
        ['case',
          ['boolean', ['feature-state', 'selected'], false],
          '#2ed573',
          '#667eea'
        ]
      ],
      'circle-stroke-color': '#fff',
      'circle-stroke-width': 2
    }
  };

  // Assemblage layer style - now using circle since assemblages are points
  const assemblageLayer = {
    id: 'assemblages',
    type: 'circle',
    paint: {
      'circle-radius': 8,
      'circle-color': '#2ed573',
      'circle-stroke-color': '#fff',
      'circle-stroke-width': 2
    }
  };

  // Parcel border layer style
  const parcelBorderLayer = {
    id: 'parcel-border',
    type: 'fill',
    paint: {
      'fill-color': '#0066cc',
      'fill-opacity': 0.3,
      'fill-outline-color': '#ff4757'
    }
  };

  // Coordinate parcel layer style (semi-transparent blue)
  const coordinateParcelLayer = {
    id: 'coordinate-parcel',
    type: 'fill',
    paint: {
      'fill-color': '#0066cc',
      'fill-opacity': 0.4
    }
  };

  // Coordinate parcel outline layer style
  const coordinateParcelOutlineLayer = {
    id: 'coordinate-parcel-outline',
    type: 'line',
    paint: {
      'line-color': '#0066cc',
      'line-width': 2
    }
  };

  // Convert parcels to GeoJSON with center points
  const parcelsGeoJSON = {
    type: 'FeatureCollection',
    features: filteredParcels
      .filter(parcel => parcel.center) // Only include parcels with center points
      .map(parcel => ({
        type: 'Feature',
        geometry: parcel.center, // Use center point instead of geometry
        properties: {
          id: parcel.parcel_id,
          parcel_id: parcel.parcel_id,
          land_acres: parcel.land_acres,
          land_use_category: parcel.land_use_category,
          zoning: parcel.zoning,
          addr: parcel.addr,
          owner: parcel.owner,
          assd_imp: parcel.assd_imp,
          type: 'parcel'
        }
      }))
  };

  // Convert assemblages to GeoJSON
  const assemblagesGeoJSON = {
    type: 'FeatureCollection',
    features: assemblages.map(assemblage => ({
      type: 'Feature',
      geometry: assemblage.center || assemblage.geometry, // Use center point if available, fallback to geometry
      properties: {
        id: assemblage.cluster_id,
        parcel_ids: assemblage.parcel_ids,
        total_acres: assemblage.total_acres,
        numberOfParcels: assemblage.numberOfParcels,
        land_use_category: assemblage.land_use_category,
        zoning: assemblage.zoning,
        addr: assemblage.addr,
        owner: assemblage.owner,
        assd_imp: assemblage.assd_imp,
        type: 'assemblage'
      }
    }))
  };

  const generateParcelBorder = async (parcelIds, state, county) => {
    try {
      setBorderLoading(true);
      const result = await api.generateParcelBorder(parcelIds, state, county);
      if (result.success) {
        setParcelBorder(result.geojson);
      }
    } catch (error) {
      console.error('Error generating parcel border:', error);
      setParcelBorder(null);
    } finally {
      setBorderLoading(false);
    }
  };

  const getParcelByCoordinates = async (latitude, longitude) => {
    // Get state and county from filters context
    let state = 'ca';
    let county = 'orange';
    
    if (filters && filters.state && filters.county) {
      // Use the state and county from filters with proper formatting
      state = filters.state;
      county = filters.county;
    } else if (searchSummary && searchSummary.applied_filters) {
      // Fallback to search summary
      state = searchSummary.applied_filters.state || state;
      county = searchSummary.applied_filters.county || county;
    }

    try {
      setCoordinateParcelLoading(true);

      const result = await api.getParcelByCoordinates(latitude, longitude, state, county);
      
      if (result.success && result.found && result.parcel) {
        setCoordinateParcel(result.parcel);
        
        // Set the parcel as selected in context
        const selectedParcelData = {
          ...result.parcel,
          type: 'parcel',
          parcel_id: result.parcel.parcel_id,
          land_acres: result.parcel.land_acres || result.parcel.total_acres || 0,
          land_use_category: result.parcel.land_use_category || 'N/A',
          zoning: result.parcel.zoning || 'N/A',
          addr: result.parcel.addr || 'N/A',
          owner: result.parcel.owner || 'N/A',
          assd_imp: result.parcel.assd_imp || 0
        };

        setSelectedParcel(selectedParcelData);
      } else {
        setCoordinateParcel(null);
        setSelectedParcel(null);
      }
    } catch (error) {
      console.error('Error getting parcel by coordinates:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        latitude,
        longitude,
        state,
        county
      });
      setCoordinateParcel(null);
      setSelectedParcel(null);
    } finally {
      setCoordinateParcelLoading(false);
    }
  };

  // Fetch tile categories when filters change
  useEffect(() => {
    const fetchTileCategories = async () => {
      if (filters && filters.state && filters.county) {
        try {
          const categories = await api.getTileCategories(filters.state, filters.county);
          setTileCategories(categories);
        } catch (error) {
          console.warn('Could not fetch tile categories:', error);
          setTileCategories(null);
        }
      }
    };

    if (tilesetVisible) {
      fetchTileCategories();
    }
  }, [filters, tilesetVisible]);

  // Fetch vector tile URL when filters change
  useEffect(() => {
    const fetchVectorTileUrl = async () => {
      if (filters && filters.state && filters.county) {
        try {
          const url = await api.getVectorTileUrl(filters.state, filters.county, 'parcels');
          setVectorTileUrl(url);
        } catch (error) {
          console.warn('Could not fetch vector tile URL:', error);
          setVectorTileUrl(null);
        }
      }
    };

    if (tilesetVisible) {
      fetchVectorTileUrl();
    }
  }, [filters, tilesetVisible]);

  // Generate color expression for zoning/land use
  const generateZoningColors = (categories, layerType) => {
    // Predefined palette of 100 attractive colors
    const colorPalette = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F',
      '#BB8FCE', '#85C1E9', '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2', '#A9DFBF',
      '#F9E79F', '#AED6F1', '#A3E4D7', '#FADBD8', '#D5DBDB', '#FCF3CF', '#D6EAF8', '#D1F2EB',
      '#FAD7A0', '#E8DAEF', '#A9CCE3', '#A3E4D7', '#F8D7DA', '#D1F2EB', '#FCF3CF', '#D6EAF8',
      '#FF5722', '#607D8B', '#795548', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4',
      '#00BCD4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFEB3B', '#FFC107', '#FF9800',
      '#FF5722', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4',
      '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFEB3B', '#FFC107', '#FF9800', '#FF5722',
      '#E74C3C', '#3498DB', '#2ECC71', '#F39C12', '#9B59B6', '#1ABC9C', '#34495E', '#E67E22',
      '#95A5A6', '#8E44AD', '#16A085', '#27AE60', '#2980B9', '#D35400', '#7F8C8D', '#C0392B',
      '#A569BD', '#5DADE2', '#58D68D', '#F4D03F', '#EB984E', '#EC7063', '#AF7AC5', '#5FAEE3',
      '#52C41A', '#FADB14', '#FA8C16', '#F5222D', '#722ED1', '#13C2C2', '#52C41A', '#FADB14',
      '#FA541C', '#F5222D', '#722ED1', '#13C2C2'
    ];

    if (!categories || !categories.categories) {
      return '#cccccc';
    }

    const categoryList = layerType === 'zoning' ? categories.categories.zoning : categories.categories.land_use;
    
    if (!categoryList || categoryList.length === 0) {
      return '#cccccc';
    }

    // Simple hash function to consistently assign colors to categories
    const hashString = (str) => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      return Math.abs(hash);
    };

    const expression = ['case'];
    
    categoryList.forEach((category, index) => {
      // Use hash for consistent color assignment or fallback to index
      const colorIndex = category ? hashString(category.toString()) % colorPalette.length : index % colorPalette.length;
      const color = colorPalette[colorIndex];
      expression.push(['==', ['get', layerType === 'zoning' ? 'zoning' : 'land_use_category'], category], color);
    });
    
    expression.push('#cccccc'); // default
    return expression;
  };

  // Zoning layer configuration
  const zoningFillLayer = {
    id: 'parcels-zoning-fill',
    type: 'fill',
    source: 'parcels-zoning',
    'source-layer': 'parcels',
    paint: {
      'fill-color': tileCategories ? (!zoningLayerVisible ? "#000000" : generateZoningColors(tileCategories, zoningLayerType)) : '#cccccc',
      'fill-opacity': !zoningLayerVisible ? 0 : 0.7
    }
  };  

  // Add a state to track current zoom
  const [currentZoom, setCurrentZoom] = useState(4);

  // Update the onMove handler to track zoom changes
  const handleMapMove = (evt) => {
    setViewState(evt.viewState);
    setCurrentZoom(evt.viewState.zoom);
  };

  // Create a function to update line width based on zoom and zoning visibility
  const updateZoningLineWidth = () => {
    if (mapRef.current) {
      if (zoningLayerVisible) {
        const map = mapRef.current.getMap();
        const newWidth = currentZoom < 14 ? 0 : 1;
        const newOpacity = currentZoom < 14 ? 1 : 0.7;
        try {
          console.log('Updating line width to:', newWidth);
          map.setPaintProperty('parcels-zoning-line', 'line-width', newWidth);
          map.setPaintProperty('parcels-zoning-fill', 'fill-opacity', newOpacity);
        } catch (error) {
          console.warn('Could not update line width:', error);
        }
      } else {
        const map = mapRef.current.getMap();
        const newWidth = currentZoom < 14 ? 0 : 1;
        try {
          map.setPaintProperty('parcels-zoning-line', 'line-width', newWidth);
        } catch (error) {
          console.warn('Could not update line width:', error);
        }
      }
    }
  };

  // Update line width when zoom or zoning visibility changes
  useEffect(() => {
    updateZoningLineWidth();
  }, [currentZoom, zoningLayerVisible]);

  // Ensure parcel markers are always on top when map style changes
  useEffect(() => {
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      
      const ensureLayerOrder = () => {
        try {
          // Define the desired layer order (bottom to top)
          const layerOrder = [
            'parcels-zoning-fill',
            'parcels-zoning-line',
            'coordinate-parcel',
            'coordinate-parcel-outline',
            'parcel-border',
            'assemblages',
            'parcel-centers' // This should always be on top
          ];
          
          // Move layers to ensure correct order
          layerOrder.forEach((layerId, index) => {
            if (map.getLayer(layerId)) {
              // Move layer to the top among our custom layers
              if (index < layerOrder.length - 1) {
                const nextLayerId = layerOrder[index + 1];
                if (map.getLayer(nextLayerId)) {
                  map.moveLayer(layerId, nextLayerId);
                }
              }
            }
          });
          
          // Ensure parcel-centers is absolutely on top
          if (map.getLayer('parcel-centers')) {
            map.moveLayer('parcel-centers');
          }
        } catch (error) {
          console.warn('Could not reorder layers:', error);
        }
      };
      
      // Run once when map loads
      if (map.loaded()) {
        ensureLayerOrder();
      } else {
        map.on('load', ensureLayerOrder);
      }
      
      // Also run when style changes
      map.on('style.load', ensureLayerOrder);
      
      return () => {
        map.off('style.load', ensureLayerOrder);
      };
    }
  }, [mapStyle]);

  // Update the zoningLineLayer to use a static initial width
  const zoningLineLayer = {
    id: 'parcels-zoning-line',
    type: 'line',
    source: 'parcels-zoning',
    'source-layer': 'parcels',
    paint: {
      'line-color': !zoningLayerVisible ? "#ff0000" : '#ffffff',
      'line-width': 0, // Initial width, will be updated dynamically
      'line-opacity': 0.8
    }
  };


  const handleMapClick = (event) => {
    const feature = event.features?.[0];
    if (feature) {
      // It's an individual parcel or assemblage
      setSelectedParcelId(feature.properties.parcel_id);
      
      // Generate parcel border for both individual parcels and assemblages
      let state = 'ca';
      let county = 'orange';
      
      // Use state and county from filters context
      if (filters && filters.state && filters.county) {
        state = filters.state;
        county = filters.county;
      } else if (searchSummary && searchSummary.applied_filters) {
        // Fallback to search summary
        state = searchSummary.applied_filters.state || state;
        county = searchSummary.applied_filters.county || county;
      }
      
      if (feature.properties.type === 'assemblage' && feature.properties.numberOfParcels) {
        // For assemblages, use the parcel IDs from the assemblage
        generateParcelBorder(feature.properties.parcel_ids, state, county);
        // Set the selected assemblage in the context
        setSelectedParcel(feature.properties);
      } else if (feature.properties.type === 'parcel') {
        // For individual parcels, use the parcel ID
        generateParcelBorder([feature.properties.parcel_id], state, county);
        // Get detailed parcel information from API
        loadParcelDetails(feature.properties.parcel_id);
      }
    } else {
      // No feature clicked, try to get parcel by coordinates
      const { lng, lat } = event.lngLat;
      
      // Clear previous selections
      setSelectedParcelId(null);
      setSelectedParcel(null);
      setParcelBorder(null);
      
      // Get parcel by coordinates
      getParcelByCoordinates(lat, lng);
    }
  };

  const handleMouseEnter = (event) => {
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      map.getCanvas().style.cursor = 'default';
      
      if (event.features && event.features[0]) {
        const feature = event.features[0];
        // Set hover state for parcels
        try {
          if (map.getSource('parcel-centers-source')) {
            map.setFeatureState(
              { source: 'parcel-centers-source', id: feature.properties.id },
              { hover: true }
            );
          }
        } catch (error) {
          console.warn('Could not set hover state for parcel:', feature.properties.id, error);
        }
      }
    }
  };

  const handleMouseLeave = (event) => {
    if (mapRef.current) {
      const map = mapRef.current.getMap();
      map.getCanvas().style.cursor = 'default';
      
      if (event.features && event.features[0]) {
        const feature = event.features[0];
        // Clear hover state for parcels
        try {
          if (map.getSource('parcel-centers-source')) {
            map.setFeatureState(
              { source: 'parcel-centers-source', id: feature.properties.id },
              { hover: false }
            );
          }
        } catch (error) {
          console.warn('Could not clear hover state for parcel:', feature.properties.id, error);
        }
      }
    }
  };



  const handleStyleChange = (newStyle) => {
    setMapStyle(newStyle);
  };

  // Add transformRequest function to handle authentication
  const transformRequest = (url, resourceType) => {
    // Only add auth headers to our API requests
    if (url.includes('/api/v1/tiles/')) {
      const token = localStorage.getItem('token');
      if (token) {
        return {
          url,
          headers: {
            'Authorization': `Bearer ${token}`
          }
        };
      }
    }
    return { url };
  };

  // Add state for legend visibility and position
  const [showLegend, setShowLegend] = useState(false);
  const [legendPosition, setLegendPosition] = useState({ x: 20, y: 200 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Add legend drag functionality
  const handleLegendMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - legendPosition.x,
      y: e.clientY - legendPosition.y
    });
  };

  const handleLegendMouseMove = (e) => {
    if (isDragging) {
      setLegendPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleLegendMouseUp = () => {
    setIsDragging(false);
  };

  // Add mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleLegendMouseMove);
      document.addEventListener('mouseup', handleLegendMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleLegendMouseMove);
        document.removeEventListener('mouseup', handleLegendMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  // Generate legend data
  const generateLegendData = () => {
    if (!tileCategories || !tileCategories.categories) {
      return [];
    }

    const categoryList = zoningLayerType === 'zoning' 
      ? tileCategories.categories.zoning 
      : tileCategories.categories.land_use;
    
    if (!categoryList || categoryList.length === 0) {
      return [];
    }

    // Use the same color palette and hash function as in generateZoningColors
    const colorPalette = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F',
      '#BB8FCE', '#85C1E9', '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2', '#A9DFBF',
      '#F9E79F', '#AED6F1', '#A3E4D7', '#FADBD8', '#D5DBDB', '#FCF3CF', '#D6EAF8', '#D1F2EB',
      '#FAD7A0', '#E8DAEF', '#A9CCE3', '#A3E4D7', '#F8D7DA', '#D1F2EB', '#FCF3CF', '#D6EAF8',
      '#FF5722', '#607D8B', '#795548', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4',
      '#00BCD4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFEB3B', '#FFC107', '#FF9800',
      '#FF5722', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4',
      '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFEB3B', '#FFC107', '#FF9800', '#FF5722',
      '#E74C3C', '#3498DB', '#2ECC71', '#F39C12', '#9B59B6', '#1ABC9C', '#34495E', '#E67E22',
      '#95A5A6', '#8E44AD', '#16A085', '#27AE60', '#2980B9', '#D35400', '#7F8C8D', '#C0392B',
      '#A569BD', '#5DADE2', '#58D68D', '#F4D03F', '#EB984E', '#EC7063', '#AF7AC5', '#5FAEE3',
      '#52C41A', '#FADB14', '#FA8C16', '#F5222D', '#722ED1', '#13C2C2', '#52C41A', '#FADB14',
      '#FA541C', '#F5222D', '#722ED1', '#13C2C2'
    ];

    const hashString = (str) => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      return Math.abs(hash);
    };

    return categoryList.map((category, index) => {
      const colorIndex = category ? hashString(category.toString()) % colorPalette.length : index % colorPalette.length;
      const color = colorPalette[colorIndex];
      return {
        category: category || 'Unknown',
        color: color
      };
    });
  };

  return (
    <MapContainer>
      <Map
        ref={mapRef}
        {...viewState}
        onMove={handleMapMove}  // Use the new handler
        style={{ width: '100%', height: '100%' }}
        mapStyle={mapStyle}
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        transformRequest={transformRequest}
        interactiveLayerIds={['parcel-centers', 'assemblages']}
        onClick={handleMapClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <NavigationControl position="top-left" />
        <MapControlGroup
          style={{
            position: 'absolute',
            top: '100px',
            left: '10px',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <IconControlButton
            aria-label={tilesetVisible ? 'Hide Tileset' : 'Show Tileset'}
            title={tilesetVisible ? 'Hide Tileset' : 'Show Tileset'}
            onClick={() => setTilesetVisible(v => !v)}
            style={{
              background: tilesetVisible ? '#d4edda' : 'white',
              borderColor: tilesetVisible ? '#c3e6cb' : '#ccc',
              color: tilesetVisible ? '#218838' : '#888',
              marginTop: '8px'
            }}
          >
            <FaLayerGroup />
          </IconControlButton>
          
          <IconControlButton
            aria-label={zoningLayerVisible ? 'Hide Zoning' : 'Show Zoning'}
            title={zoningLayerVisible ? 'Hide Zoning' : 'Show Zoning'}
            onClick={() => setZoningLayerVisible(v => !v)}
            style={{
              background: zoningLayerVisible ? '#e1f5fe' : 'white',
              borderColor: zoningLayerVisible ? '#81d4fa' : '#ccc',
              color: zoningLayerVisible ? '#0277bd' : '#888',
              marginTop: '8px'
            }}
          >
            <FaMapMarkedAlt />
          </IconControlButton>
          
          {zoningLayerVisible && (
            <CheckboxContainer>
              <CheckboxLabel>
                <input
                  type="radio"
                  name="zoningLayerType"
                  value="zoning"
                  checked={zoningLayerType === 'zoning'}
                  onChange={(e) => setZoningLayerType(e.target.value)}
                />
                Zoning
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="radio"
                  name="zoningLayerType"
                  value="land_use_category"
                  checked={zoningLayerType === 'land_use_category'}
                  onChange={(e) => setZoningLayerType(e.target.value)}
                />
                Land Use
              </CheckboxLabel>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={showLegend}
                  onChange={(e) => setShowLegend(e.target.checked)}
                />
                Show Legend
              </CheckboxLabel>
            </CheckboxContainer>
          )}
          
          <IconControlButton
            aria-label="Reset View"
            title="Reset View"
            onClick={() => setViewState({
              longitude: -98.5795,
              latitude: 39.8283,
              zoom: 4
            })}
          >
            <FaCompass />
          </IconControlButton>
        </MapControlGroup>
        
        {/* Parcels Raster Tileset Layer - Background display only */}
        {/* {tilesetVisible && isValidTilesetSource && tilesetLayer && (
          <Source
            id={tilesetSource.id}
            type="raster"
            tiles={tilesetSource.tiles}
            tileSize={tilesetSource.tileSize}
          >
            <Layer {...tilesetLayer} />
          </Source>
        )} */}

        {/* Vector Tiles Zoning Layer - Render first (bottom) */}
        {tilesetVisible && filters && filters.state && filters.county && vectorTileUrl && (
          <Source
            id="parcels-zoning"
            type="vector"
            tiles={[vectorTileUrl]}
            minzoom={4}
            maxzoom={18}
          >
            <Layer {...zoningFillLayer} />
            <Layer {...zoningLineLayer} />
          </Source>
        )}
        
        {/* Coordinate Parcel Layer - Below borders */}
        {coordinateParcel && coordinateParcel.geometry && (
          <Source 
            id="coordinate-parcel-source" 
            type="geojson" 
            data={{
              type: 'Feature',
              geometry: coordinateParcel.geometry,
              properties: coordinateParcel
            }}
          >
            <Layer {...coordinateParcelLayer} />
            <Layer {...coordinateParcelOutlineLayer} />
          </Source>
        )}
        
        {/* Parcel Border Layer - Above coordinate parcels */}
        {parcelBorder && (
          <Source type="geojson" data={parcelBorder}>
            <Layer {...parcelBorderLayer} />
          </Source>
        )}
        
        {/* Assemblages Layer - Near top for interaction */}
        {assemblages.length > 0 && (
          <Source id="assemblages-source" type="geojson" data={assemblagesGeoJSON}>
            <Layer {...assemblageLayer} />
          </Source>
        )}

        {/* Parcel Center Points Layer - Always render LAST (on top) for interaction */}
        {filteredParcels.length > 0 && parcelsGeoJSON.features.length > 0 && (
          <Source 
            id="parcel-centers-source"
            type="geojson" 
            data={parcelsGeoJSON}
          >
            <Layer {...parcelCenterLayer} />
          </Source>
        )}
      </Map>
      
      <MapStyleSelector 
        currentStyle={mapStyle}
        onStyleChange={handleStyleChange}
      />

      {showLegend && (
        <LegendModal
          style={{
            left: legendPosition.x,
            top: legendPosition.y
          }}
          onMouseDown={handleLegendMouseDown}
        >
          <LegendHeader>
            {zoningLayerType === 'zoning' ? 'Zoning Legend' : 'Land Use Legend'}
            <CloseButton onClick={() => setShowLegend(false)}>
              ×
            </CloseButton>
          </LegendHeader>
          <LegendContent>
            {generateLegendData().map((item, index) => (
              <LegendItem key={index}>
                <LegendColorBox 
                  style={{ backgroundColor: item.color }}
                  opacity={currentZoom >= 14 ? 0.7 : 1}
                />
                {item.category}
              </LegendItem>
            ))}
          </LegendContent>
        </LegendModal>
      )}
    </MapContainer>
  );
};

export default ParcelMap; 